import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesAPI } from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

const QUESTIONS = [
  { emoji: '🐘', question: 'What animal is this?', options: ['Elephant 🐘', 'Horse 🐴', 'Cow 🐄', 'Bear 🐻'], answer: 0 },
  { emoji: '🍕', question: 'What food is this?', options: ['Burger 🍔', 'Pizza 🍕', 'Sandwich 🥪', 'Hot Dog 🌭'], answer: 1 },
  { emoji: '🚀', question: 'What is this?', options: ['Airplane ✈️', 'Helicopter 🚁', 'Rocket 🚀', 'Satellite 🛸'], answer: 2 },
  { emoji: '🌈', question: 'What is this?', options: ['Cloud ☁️', 'Lightning ⚡', 'Rainbow 🌈', 'Star ⭐'], answer: 2 },
  { emoji: '🦁', question: 'What animal is this?', options: ['Tiger 🐯', 'Lion 🦁', 'Leopard 🐆', 'Fox 🦊'], answer: 1 },
  { emoji: '🎸', question: 'What instrument is this?', options: ['Piano 🎹', 'Drums 🥁', 'Guitar 🎸', 'Violin 🎻'], answer: 2 },
  { emoji: '🍓', question: 'What fruit is this?', options: ['Blueberry 🫐', 'Strawberry 🍓', 'Cherry 🍒', 'Watermelon 🍉'], answer: 1 },
  { emoji: '🏔️', question: 'What is this?', options: ['Volcano 🌋', 'Hill 🏕️', 'Mountain 🏔️', 'Island 🏝️'], answer: 2 },
  { emoji: '🐬', question: 'What animal is this?', options: ['Shark 🦈', 'Dolphin 🐬', 'Whale 🐋', 'Octopus 🐙'], answer: 1 },
  { emoji: '🎃', question: 'What is this?', options: ['Birthday Cake 🎂', 'Jack-o-Lantern 🎃', 'Lamp 💡', 'Ball 🎯'], answer: 1 },
];

const BLUR_LEVELS = [40, 25, 12, 0];

export default function GuessPictureScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();

  const [questions] = useState(() => [...QUESTIONS].sort(() => Math.random() - 0.5).slice(0, 6));
  const [current, setCurrent] = useState(0);
  const [blurLevel, setBlurLevel] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(null);

  const q = questions[current];

  const reveal = () => {
    if (blurLevel < BLUR_LEVELS.length - 1) setBlurLevel(b => b + 1);
  };

  const handleAnswer = useCallback((idx) => {
    if (selected !== null) return;
    setSelected(idx);
    const correct = idx === q.answer;
    const pointsForAnswer = correct ? (BLUR_LEVELS.length - blurLevel) * 25 : 0;
    if (correct) setScore(s => s + pointsForAnswer);
    setTimeout(() => {
      if (current + 1 >= questions.length) {
        setGameOver(true);
        saveResult(score + pointsForAnswer);
      } else {
        setCurrent(c => c + 1);
        setBlurLevel(0);
        setSelected(null);
      }
    }, 1200);
  }, [selected, q, blurLevel, current, score, questions.length]);

  const saveResult = async (finalScore) => {
    if (!profile) return;
    try {
      const result = await gamesAPI.saveResult({ profileId: profile.id, gameId: 'guess-the-picture', score: finalScore, metadata: { questions: questions.length } });
      setPointsEarned(result.pointsEarned);
      refreshProfile();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #4ECDC4, #26A69A)', paddingBottom: 20 }}>
        <button className="btn-back" onClick={() => navigate('/games')} style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem' }}>🎨 Guess the Picture!</h1>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>Question {current + 1}/{questions.length} · Score: {score}</div>
        </div>
      </div>

      <div className="screen-body" style={{ paddingTop: 24 }}>
        {gameOver ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '5rem', marginBottom: 12 }}>🎉</div>
            <h2 style={{ fontFamily: 'var(--font-title)', color: 'var(--purple)', fontSize: '1.8rem' }}>Great job!</h2>
            <p style={{ color: 'var(--gray)', margin: '10px 0 20px' }}>Your final score: <strong>{score}</strong></p>
            {pointsEarned && (
              <div style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', borderRadius: 14, padding: 12, marginBottom: 20 }}>
                <strong>⭐ +{pointsEarned} points earned!</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => { setCurrent(0); setBlurLevel(0); setSelected(null); setScore(0); setGameOver(false); setPointsEarned(null); }}>Play Again</button>
              <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => navigate('/games')}>Games</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: '8rem', filter: `blur(${BLUR_LEVELS[blurLevel]}px)`, transition: 'filter 0.5s ease', userSelect: 'none', lineHeight: 1.2, marginBottom: 12 }}>
                {q.emoji}
              </div>
              <button onClick={reveal} disabled={blurLevel === BLUR_LEVELS.length - 1} style={{ background: blurLevel === BLUR_LEVELS.length - 1 ? 'var(--bg)' : 'var(--orange)', color: blurLevel === BLUR_LEVELS.length - 1 ? 'var(--gray)' : 'white', border: 'none', borderRadius: 12, padding: '8px 20px', fontFamily: 'var(--font-body)', fontWeight: 700, cursor: blurLevel === BLUR_LEVELS.length - 1 ? 'default' : 'pointer', fontSize: '0.85rem' }}>
                {blurLevel === BLUR_LEVELS.length - 1 ? '✅ Clear now!' : '🔍 Make it clearer (fewer points)'}
              </button>
            </div>
            <h3 style={{ fontFamily: 'var(--font-title)', textAlign: 'center', marginBottom: 16, fontSize: '1.2rem' }}>{q.question}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {q.options.map((opt, i) => {
                let bg = 'white', border = 'var(--border)', color = 'var(--dark)';
                if (selected !== null) {
                  if (i === q.answer) { bg = '#E8F5E9'; border = 'var(--green)'; }
                  else if (i === selected && selected !== q.answer) { bg = '#FFEBEE'; border = 'var(--red)'; }
                }
                return (
                  <button key={i} onClick={() => handleAnswer(i)} style={{ padding: '14px 20px', border: `3px solid ${border}`, borderRadius: 16, background: bg, color, fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1rem', cursor: selected !== null ? 'default' : 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                    {opt}{selected !== null && i === q.answer ? ' ✅' : ''}{selected === i && selected !== q.answer ? ' ❌' : ''}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
